import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Building2, FileStack, FileText, MessageCircleQuestion, ArrowLeftRight, Search, CornerDownLeft } from 'lucide-react';
import type { SearchResultDTO } from '@btw/shared';
import { useSearch } from '../api/hooks';
import { useDebounced, useFocusTrap } from '../lib/hooks';
import { useT } from '../i18n';
import { Skeleton } from '../components/Feedback';

const TYPE_ORDER: SearchResultDTO['type'][] = ['entity', 'period', 'question', 'document', 'transaction'];
const TYPE_ICON = { entity: Building2, period: FileText, question: MessageCircleQuestion, document: FileStack, transaction: ArrowLeftRight } as const;

/** ⌘K / Ctrl K palette: entities, returns, documents, questions, amounts ("142,50") or supplier names (§4.2). */
export function SearchPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const debounced = useDebounced(q, 200);
  const search = useSearch(debounced);
  useFocusTrap(ref, open, onClose);

  useEffect(() => {
    if (!open) {
      setQ('');
      setActive(0);
    }
  }, [open]);

  const groups = useMemo(() => {
    const items = search.data ?? [];
    return TYPE_ORDER.map((type) => ({ type, items: items.filter((i) => i.type === type) })).filter((g) => g.items.length);
  }, [search.data]);
  const flat = groups.flatMap((g) => g.items);

  useEffect(() => setActive(0), [debounced]);

  const go = (r: SearchResultDTO | undefined) => {
    if (!r) return;
    onClose();
    navigate(r.href);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(flat.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flat[active] ?? flat[0]);
    }
  };

  if (!open) return null;
  let index = -1;
  const tooShort = debounced.trim().length < 2;
  return createPortal(
    <>
      <div className="overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        className="dialog dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-label={t('search.title')}
        style={{ top: '12vh', transform: 'translateX(-50%)', padding: 0 }}
      >
        <div className="row" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-default)', flexWrap: 'nowrap' }}>
          <Search size={20} aria-hidden="true" color="var(--text-secondary)" />
          <input
            className="input"
            style={{ border: 0, background: 'transparent', outline: 'none', paddingLeft: 0 }}
            placeholder={t('search.placeholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            role="combobox"
            aria-expanded={flat.length > 0}
            aria-controls={listId}
            aria-activedescendant={flat[active] ? `${listId}-${active}` : undefined}
            aria-autocomplete="list"
            data-autofocus
          />
          <kbd>Esc</kbd>
        </div>
        <div id={listId} role="listbox" aria-label={t('search.results')} style={{ maxHeight: '60vh', overflow: 'auto', padding: 8 }}>
          {tooShort && <p className="muted" style={{ padding: 16 }}>{t('search.hint')}</p>}
          {!tooShort && search.isFetching && !search.data && (
            <div className="stack-sm" style={{ padding: 16 }}>
              <Skeleton h={18} w="60%" />
              <Skeleton h={18} w="40%" />
            </div>
          )}
          {!tooShort && search.data && flat.length === 0 && <p className="muted" style={{ padding: 16 }}>{t('search.none', { q: debounced })}</p>}
          {groups.map((g) => {
            const Icon = TYPE_ICON[g.type];
            return (
              <div key={g.type} role="group" aria-label={t(`search.type.${g.type}`)}>
                <div className="menu-label">{t(`search.type.${g.type}`)}</div>
                {g.items.map((r) => {
                  index++;
                  const i = index;
                  return (
                    <div
                      key={`${r.type}-${r.id}`}
                      id={`${listId}-${i}`}
                      role="option"
                      aria-selected={i === active}
                      className="menu-item"
                      style={{ background: i === active ? 'var(--bg-sunken)' : undefined, minHeight: 52 }}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(r)}
                    >
                      <Icon size={18} aria-hidden="true" />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontWeight: 500 }}>{r.title}</span>
                        <span className="muted small">{r.subtitle}</span>
                      </span>
                      {i === active && <CornerDownLeft size={16} aria-hidden="true" />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </>,
    document.body,
  );
}
